import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "count", "omFilter", "stateFilter", "frequencyFilter",
                    "priorityFilter", "tisFilter", "visitedFilter", "pageSizeFilter",
                    "storeSearch", "missingSearch", "completedSearch",
                    "summaryAll", "summaryFrequency", "summaryManager", "summaryState",
                    "paginationInfo"]

  connect() {
    this.applyFilters()
  }

  filter() {
    this.applyFilters()
  }

  resetFilters() {
    if (this.hasOmFilterTarget) this.omFilterTarget.value = ""
    if (this.hasStateFilterTarget) this.stateFilterTarget.value = ""
    if (this.hasFrequencyFilterTarget) this.frequencyFilterTarget.value = ""
    if (this.hasPriorityFilterTarget) this.priorityFilterTarget.value = ""
    if (this.hasTisFilterTarget) this.tisFilterTarget.value = ""
    if (this.hasVisitedFilterTarget) this.visitedFilterTarget.value = ""
    if (this.hasStoreSearchTarget) this.storeSearchTarget.value = ""
    if (this.hasMissingSearchTarget) this.missingSearchTarget.value = ""
    if (this.hasCompletedSearchTarget) this.completedSearchTarget.value = ""
    this.applyFilters()
  }

  applyFilters() {
    const om = this.hasOmFilterTarget ? this.omFilterTarget.value : ""
    const state = this.hasStateFilterTarget ? this.stateFilterTarget.value : ""
    const frequency = this.hasFrequencyFilterTarget ? this.frequencyFilterTarget.value : ""
    const priority = this.hasPriorityFilterTarget ? this.priorityFilterTarget.value : ""
    const tis = this.hasTisFilterTarget ? this.tisFilterTarget.value : ""
    const visited = this.hasVisitedFilterTarget ? this.visitedFilterTarget.value : ""
    const pageSize = this.hasPageSizeFilterTarget ? this.pageSizeFilterTarget.value : "200"
    const storeSearch = this.hasStoreSearchTarget ? this.storeSearchTarget.value.toLowerCase() : ""
    const missingSearch = this.hasMissingSearchTarget ? this.missingSearchTarget.value.toLowerCase() : ""
    const completedSearch = this.hasCompletedSearchTarget ? this.completedSearchTarget.value.toLowerCase() : ""

    let visibleCount = 0
    let totalCount = 0
    const limit = pageSize === "all" ? Infinity : parseInt(pageSize, 10)

    // Summary counters
    const summaryAll = { stores: 0, visited: 0, pct80: 0, tisDone: 0, pct80Tis: 0 }
    const summaryByFreq = {}
    const summaryByManager = {}
    const summaryByState = {}

    this.rowTargets.forEach(row => {
      totalCount++
      let show = true

      if (om && row.dataset.om !== om) show = false
      if (state && row.dataset.stateGroup !== state) show = false
      if (frequency && row.dataset.frequency !== frequency) show = false
      if (tis && row.dataset.tisStatus !== tis) show = false

      if (priority) {
        const rank = parseInt(row.dataset.priorityRank, 10)
        const maxRank = parseInt(priority, 10)
        if (isNaN(rank) || rank > maxRank) show = false
      }

      if (visited === "visited" && row.dataset.visited === "false") show = false
      if (visited === "not_visited" && row.dataset.visited === "true") show = false

      if (storeSearch) {
        const haystack = (row.dataset.storeName + " " + row.dataset.repName).toLowerCase()
        if (!haystack.includes(storeSearch)) show = false
      }

      if (missingSearch) {
        const missing = (row.dataset.missingMissions || "").toLowerCase()
        if (!missing.includes(missingSearch)) show = false
      }

      if (completedSearch) {
        const completed = (row.dataset.completedMissions || "").toLowerCase()
        if (!completed.includes(completedSearch)) show = false
      }

      if (show) {
        // Accumulate summary stats from filtered rows
        const pct = parseFloat(row.dataset.pct)
        const isDone = row.dataset.tisDone === "true"
        const isVisited = row.dataset.visited === "true"
        const freq = row.dataset.frequencyLabel
        const mgr = row.dataset.om
        const sg = row.dataset.stateGroup

        summaryAll.stores++
        if (isVisited) summaryAll.visited++
        if (pct >= 80) summaryAll.pct80++
        if (isDone) summaryAll.tisDone++
        if (pct >= 80 && isDone) summaryAll.pct80Tis++

        // By frequency
        if (!summaryByFreq[freq]) summaryByFreq[freq] = { stores: 0, visited: 0, pct80: 0, tisDone: 0, pct80Tis: 0 }
        summaryByFreq[freq].stores++
        if (isVisited) summaryByFreq[freq].visited++
        if (pct >= 80) summaryByFreq[freq].pct80++
        if (isDone) summaryByFreq[freq].tisDone++
        if (pct >= 80 && isDone) summaryByFreq[freq].pct80Tis++

        // By manager
        if (!summaryByManager[mgr]) summaryByManager[mgr] = { stores: 0, visited: 0, pct80: 0, tisDone: 0, pct80Tis: 0 }
        summaryByManager[mgr].stores++
        if (isVisited) summaryByManager[mgr].visited++
        if (pct >= 80) summaryByManager[mgr].pct80++
        if (isDone) summaryByManager[mgr].tisDone++
        if (pct >= 80 && isDone) summaryByManager[mgr].pct80Tis++

        // By state group
        if (!summaryByState[sg]) summaryByState[sg] = { stores: 0, visited: 0, pct80: 0, tisDone: 0, pct80Tis: 0 }
        summaryByState[sg].stores++
        if (isVisited) summaryByState[sg].visited++
        if (pct >= 80) summaryByState[sg].pct80++
        if (isDone) summaryByState[sg].tisDone++
        if (pct >= 80 && isDone) summaryByState[sg].pct80Tis++

        if (visibleCount < limit) {
          row.classList.remove("hidden")
          visibleCount++
        } else {
          row.classList.add("hidden")
        }
      } else {
        row.classList.add("hidden")
      }
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = `Showing ${visibleCount} of ${summaryAll.stores} stores (${totalCount} total)`
    }

    this.updateSummaryTable(this.summaryAllTargets, summaryAll)
    this.updateSummaryGroup(this.summaryFrequencyTargets, summaryByFreq)
    this.updateSummaryGroup(this.summaryManagerTargets, summaryByManager)
    this.updateSummaryGroup(this.summaryStateTargets, summaryByState)
  }

  updateSummaryTable(targets, data) {
    targets.forEach(el => {
      const field = el.dataset.summaryField
      if (field && data[field] !== undefined) {
        el.textContent = data[field]
      }
    })
  }

  updateSummaryGroup(targets, groupData) {
    targets.forEach(el => {
      const group = el.dataset.summaryGroup
      const field = el.dataset.summaryField
      if (group && field && groupData[group]) {
        el.textContent = groupData[group][field]
      } else if (group && field) {
        el.textContent = "0"
      }
    })
  }
}
